import { Trans, t } from "@lingui/macro";
import { Loader2, Trash2, FileText, PlusCircle, MinusCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from "react";
import { Soribot } from "../../models/Soribot";
import { SourceDocument } from "../../models/SoribotSource";
import { DocumentLoader } from "../../models/local/Types";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { getSourceInfo } from "./SoribotDatasources";
import SourceDocEditor from "./SourceDocEditor";
import { UploadFileSection, UploadedFile } from "./compontents/UploadFileSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Progress } from "../../components/ui/progress";
import { IconPlus } from '../components/FigmaIcons';

export const SoribotDatasourcesDocs: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [openedDoc, setOpenedDoc] = useState<SourceDocument | null>(null);
  const [loader, setLoader] = useState<DocumentLoader>('PDFLoader');
  const { toast } = useToast();
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: () => void }>({ open: false, title: '', message: '', confirm: () => { } });

  return <>
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue} className="w-full bg-background rounded border mb-4">
      <AccordionItem value="add-doc" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center text-sm font-semibold"><IconPlus className="w-4 h-4 mr-2" /> <Trans>Add knowledge docs</Trans></div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="p-4 border rounded bg-muted/50 text-center space-y-4">
            <Button variant="outline" className="w-full" onClick={createEmptyDoc}><Trans>Create empty doc</Trans></Button>
            <div className="border-t my-2"></div>
            <UploadFileSection storageFolderPath={`/${Soribot.COL}/${bot.id}/sourcedocs`} onFilesUploaded={onFilesUploadedHandler} />

            <div className="max-w-md mx-auto p-4 border rounded bg-background mt-4 text-left">
              <Label className="mb-2 block font-medium"><Trans>Selected PDF Loader</Trans></Label>
              <RadioGroup value={loader} onValueChange={(v) => setLoader(v as DocumentLoader)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="PDFLoader" id="r1" />
                  <Label htmlFor="r1"><Trans>LangChain</Trans></Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="UnstructuredLoader" id="r2" />
                  <Label htmlFor="r2"><Trans>Unstructured.io</Trans></Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>

    <SourceDocumentList bot={bot} openDoc={tryOpenedDoc} />
    {openedDoc && <SourceDocEditor bot={bot} sd={openedDoc} onClose={() => setOpenedDoc(null)} chunkNr={-1} />}

    <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alert.title}</AlertDialogTitle>
          <AlertDialogDescription>{alert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
          <AlertDialogAction onClick={alert.confirm} className="bg-destructive hover:bg-destructive/90"><Trans>Confirm</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;

  async function createEmptyDoc() {
    const newDoc = new SourceDocument(UUID.get(), bot.id, My.user.id, "")
    setOpenedDoc(newDoc);
  }

  async function onFilesUploadedHandler(files: UploadedFile[]) {
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const newDoc = new SourceDocument(UUID.get(), bot.id, My.user.id, f.fileName);
        newDoc.sourceFileUrl = f.fileUrl;
        newDoc.sourceFileName = f.fileName;
        toast({ title: t`Just a moment...` })
        await Fire.bot.setSourceDocWithFile({ sd: newDoc, loader: loader });
        toast({ title: t`${f.fileName} is uploaded and ready for editing and syncing.` })
        setAccordionValue(undefined);
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: error.message });
    }
  }

  function tryOpenedDoc(sd: SourceDocument) {
    if (sd.inProgress) {
      setAlert({
        open: true,
        title: t`Document is processing`,
        message: <div dangerouslySetInnerHTML={{ __html: t`Do you want to interrupt the process and delete this document?` + `<br/><br/>${sd.title}` }} />,
        confirm: async () => {
          Fire.bot.deleteSources([sd]);
          setAlert(prev => ({ ...prev, open: false }));
        }
      });
    } else {
      setOpenedDoc(sd);
    }
  }
}

const SourceDocumentList: React.FC<{ bot: Soribot, openDoc: (sd: SourceDocument) => void }> = ({ bot, openDoc }) => {
  const [inProgress, setInProgress] = useState(false);
  const [sourceDocs, setSourceDocs] = useState<SourceDocument[]>([]);
  const [inProgressUpdate, setInProgressUpdate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, sd: SourceDocument | null }>({ open: false, sd: null });

  useEffect(() => {
    setInProgress(true);
    return Fire.bot.subscribeToSourceObjects(bot.id, 'source-doc', (list: SourceDocument[]) => {
      setSourceDocs(list.sort((a, b) => Util.dateSortFunc(a.created, b.created)));
      setInProgress(false);
    });
  }, [bot.id]);

  return <>
    {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>}
    {!inProgress && <>
      {sourceDocs.length > 0 && <div className="space-y-2">
        <h4 className="text-sm font-bold text-muted-foreground px-2 mt-4 uppercase"><Trans>Added docs</Trans></h4>
        {sourceDocs.map(SourceDocItem)}
      </div>}
      {sourceDocs.length === 0 && <div className="p-4 text-center text-muted-foreground"><Trans>No docs added</Trans></div>}
      <br />
    </>}

    <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle><Trans>Remove doc</Trans></AlertDialogTitle>
          <AlertDialogDescription><Trans>Are you sure you want to remove this doc?</Trans></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
          <AlertDialogAction onClick={() => {
            if (deleteConfirm.sd) {
              setInProgressUpdate(true);
              Fire.bot.deleteSources([deleteConfirm.sd]).then(() => setInProgressUpdate(false));
              setDeleteConfirm(prev => ({ ...prev, open: false }));
            }
          }} className="bg-destructive hover:bg-destructive/90"><Trans>Remove</Trans></AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;

  function SourceDocItem(sd: SourceDocument) {
    return <div key={sd.id} className={`flex items-center justify-between p-3 rounded border hover:bg-muted/50 cursor-pointer ${sd.inProgress ? 'bg-muted/50' : 'bg-background'}`}>
      <div className="flex-1 min-w-0" onClick={() => openDoc(sd)}>
        <div className="flex items-center gap-2 font-medium truncate">
          <FileText className="w-4 h-4 text-blue-500 shrink-0" />
          <span className="truncate">{sd.title}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">{getSourceInfo(false, sd)}</div>
      </div>
      {sd.inProgress || inProgressUpdate ?
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground shrink-0" />
        :
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0" onClick={() => setDeleteConfirm({ open: true, sd })}>
          <MinusCircle className="w-5 h-5" />
        </Button>
      }
    </div>;
  }
}
